/**
 * One way for the console to write: call a route through the client, refresh what it changed, and say why it failed in the
 * content §2.7 sentence. A `STALE_STATE` refresh happens on every failure, so the board is never left showing old state.
 */
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BodyOf, ErrorBody, ResponseOf, RouteName } from "@bsim/contracts";
import type { ApiClient } from "../api/client";
import type { ApiResult, RouteParams } from "../api/types";
import { errorSentence } from "../errors";
import { useToast } from "../theme/Toast";

/** Views that no facilitator write changes. */
const STABLE: ReadonlySet<string> = new Set(["authMe", "contentCatalog"]);

const idempotencyKey = (): Record<string, string> => ({ "idempotency-key": globalThis.crypto.randomUUID() });

/**
 * Only some refusals carry a message written as a plain sentence for the person (a field the entry names, a rescue that is not allowed,
 * a check that is not open); every other message is developer text and stays under "Details" (GxErrorBanner), never in the toast.
 */
const PLAIN_CODES: ReadonlySet<string> = new Set(["RESCUE_NOT_ALLOWED", "ASSESSMENT_NOT_OPEN"])
/** A refused move or a forbidden act carries a plain sentence from the api (H-API); an older api's developer text ("RELEASE_SURPRISE is not allowed while …") fails the shape test and stays under Details. */
const SENTENCE_CODES: ReadonlySet<string> = new Set(["INVALID_TRANSITION", "FORBIDDEN"]);
/** A plain sentence: a capital first, a full stop last, and none of an id's shapes (an underscore, camelCase, `key:value` or `a.b` after a colon, an all-caps word over three letters, a bare role id). */
export const looksPlain = (message: string): boolean => {
  const m = message.trim();
  return /^[A-Z][^.!?]*\.$/.test(m) && !/_|[a-z][A-Z][a-z]|\w:\w|:\s*\w+\.\w|\b[A-Z]{4,}\b|\b(?:ceo|cro|cfo)\b/.test(m);
};
export const plainMessage = (error: ErrorBody["error"]): boolean =>
  error.message.trim() !== ""
  && (PLAIN_CODES.has(error.code) || (SENTENCE_CODES.has(error.code) && looksPlain(error.message)) || (error.code === "VALIDATION" && error.details?.["field"] !== undefined)
    // A refused round move names the teams it waits on (`details.teamIds`, the strategy guard); the machine's own fallback ("roundOpen refused: …") has only `details.guards` and stays under Details.
    || (error.code === "GUARD_FAILED" && Array.isArray(error.details?.["teamIds"])));

export type Act = <N extends RouteName>(name: N, params: RouteParams<N>, body: BodyOf<N>, opts?: { readonly idempotent?: boolean; readonly headers?: Readonly<Record<string, string>> }) => Promise<ApiResult<ResponseOf<N>>>;

export function useAct(api: ApiClient): Act {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useCallback<Act>(async (name, params, body, opts = {}) => {
    const result = await api.call(name, params, body, { ...(opts.idempotent === true ? idempotencyKey() : {}), ...opts.headers });
    // Failures show the content §2.7 sentence (incl. STALE_STATE) — screens §F1 words STALE_STATE differently and content.md wins.
    // A plain-sentence message from the api follows the content sentence; developer text does not reach the toast.
    if (!result.ok) {
      const sentence = errorSentence(result.error);
      toast(plainMessage(result.error) ? `${sentence} ${result.error.message.trim()}` : sentence);
    }
    // Every write can move the board, the room screen or the finale; the cache refetches only what is mounted.
    // The board refetches first and is the only one awaited, so it shows the new state as the action returns; the rest refresh in the background.
    const board = queryClient.invalidateQueries({ queryKey: ["cohortBoard"] });
    void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "cohortBoard" && !STABLE.has(String(q.queryKey[0])) });
    await board;
    return result;
  }, [api, queryClient, toast]);
}
